/**
 * Client-side extraction service
 * VERSION: 1.0.7 - ULTRA-FAST MODE (0.75x scale, 512 tokens, 0.8 quality)
 */

import * as pdfjsLib from 'pdfjs-dist';

// Use local worker in public folder (avoids CSP issues with CDN)
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.mjs';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://provider-portal-api-production.gustavdatadc.workers.dev';
const OLLAMA_URL = `${API_BASE_URL}/api/ollama`; // Use API proxy instead of direct tunnel
const OLLAMA_BASE_URL = 'https://ollama.datadc-pr.com'; // For health check only

/**
 * Extract document - ALL files go through Llama 3.2 Vision
 * PDFs are converted to images first
 */
export async function extractDocumentWithOllama(file, apiToken) {
  console.log('%c[OLLAMA] ========== EXTRACTION START ==========', 'background: #0057B8; color: white; font-weight: bold;');
  console.log('[OLLAMA] File:', file.name, 'Type:', file.type, 'Size:', file.size);
  console.log('[OLLAMA] Token provided:', !!apiToken);

  const isPDF = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
  const isImage = file.type.startsWith('image/');

  try {
    if (isPDF) {
      console.log('[OLLAMA] Detected PDF - converting to image for Vision OCR...');
      const imageFile = await convertPDFToImage(file);
      console.log('[OLLAMA] PDF converted to image, now using Llama 3.2 Vision...');
      return await extractImageWithVision(imageFile, apiToken);
    } else if (isImage) {
      console.log('[OLLAMA] Detected IMAGE - using Llama 3.2 Vision');
      return await extractImageWithVision(file, apiToken);
    } else {
      throw new Error('Unsupported file type. Please upload PDF or image (PNG/JPG)');
    }
  } catch (error) {
    console.error('[OLLAMA] Extraction failed:', error.message);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Convert PDF file to PNG image (first page)
 */
async function convertPDFToImage(file) {
  console.log('[OLLAMA] Loading PDF...');

  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

  console.log('[OLLAMA] PDF loaded, pages:', pdf.numPages);

  // Get first page
  const page = await pdf.getPage(1);

  // ULTRA-FAST MODE: Reduced scale for speed (0.75x)
  const scale = 0.75;
  const viewport = page.getViewport({ scale });

  // Create canvas
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  canvas.width = viewport.width;
  canvas.height = viewport.height;

  console.log(`[OLLAMA] Rendering PDF page ${viewport.width}x${viewport.height}...`);

  // Render PDF page to canvas
  await page.render({
    canvasContext: context,
    viewport: viewport
  }).promise;

  // Convert canvas to blob
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        const imageFile = new File([blob], 'converted.png', { type: 'image/png' });
        console.log('[OLLAMA] PDF converted to PNG:', imageFile.size, 'bytes');
        resolve(imageFile);
      } else {
        reject(new Error('Failed to convert PDF to image'));
      }
    }, 'image/png', 0.8);
  });
}

/**
 * Extract image using Llama 3.2 Vision - returns raw text
 */
async function extractImageWithVision(file, apiToken) {
  const base64Data = await fileToBase64(file);
  console.log('[OLLAMA] Image base64 length:', base64Data.length);

  const prompt = `Extract ALL text from this medical document image. Return the complete text content exactly as it appears. Include patient names, dates, procedures, charges, codes, and all other medical information. Preserve the layout and structure as much as possible. Return only the extracted text, no other commentary.`;

  const response = await fetch(OLLAMA_URL, {
    method: 'POST',
    headers: { 
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${apiToken}`
    },
    body: JSON.stringify({
      model: 'llama3.2-vision:latest',
      prompt: prompt,
      images: [base64Data],
      stream: false,
      options: {
        temperature: 0.1,
        num_predict: 512, // ULTRA-FAST: Limit to ~400 tokens for fastest response
        num_ctx: 2048
      }
    })
  });
  
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Vision model HTTP ${response.status}: ${errorText.substring(0, 200)}`);
  }
  
  const result = await response.json();
  const rawText = result.response || '';
  
  console.log('[OLLAMA] Raw text extracted:', rawText.substring(0, 500) + '...');
  console.log('%c[OLLAMA] ========== SUCCESS ==========', 'background: #00C853; color: white; font-weight: bold;');
  
  return {
    success: true,
    rawText: rawText,
    data: { rawText } // For backward compatibility
  };
}

/**
 * Check if Ollama is available via tunnel
 */
export async function checkOllamaAvailable() {
  console.log('[OLLAMA] Checking availability...');

  try {
    const response = await fetch(`${OLLAMA_BASE_URL}/api/tags`, {
      method: 'GET',
      headers: { 'Accept': 'application/json' },
      signal: AbortSignal.timeout(5000)
    });

    if (!response.ok) {
      console.error('[OLLAMA] Availability check failed:', response.status);
      return false;
    }

    const data = await response.json();
    const hasVision = data.models?.some(m => m.name?.includes('vision') || m.name?.includes('llava'));

    console.log('[OLLAMA] Available - Vision model:', hasVision);
    return hasVision;

  } catch (e) {
    console.error('[OLLAMA] Availability check error:', e.message);
    return false;
  }
}

// Helper functions
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === 'string') {
        resolve(result.split(',')[1]);
      } else {
        reject(new Error('FileReader failed'));
      }
    };
    reader.onerror = (e) => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

function normalizeCodes(codes) {
  if (!Array.isArray(codes)) return [];
  return codes.filter(c => c).map(c =>
    typeof c === 'string' ? { code: c, description: '' } : { code: c.code || '', description: c.description || '' }
  );
}

function calculateConfidence(data) {
  let score = 0;
  if (data.patientName) score += 25;
  if (data.serviceDate) score += 25;
  if (data.procedureName || data.cptCodes?.length > 0) score += 25;
  if (data.totalCharge > 0) score += 25;
  return Math.min(100, score);
}

export default { extractDocumentWithOllama, checkOllamaAvailable };
